import { useState } from 'react'
import type { DistanceUnits } from '~/data/types'
import { cmToFeetInches, feetInchesToCm } from '~/lib/units'
import { UnitInput } from '~/components/unit-input'

/**
 * The fields a height is typed into, following the distance units — see Units in the root
 * spec: one field in centimetres with kilometres, two — feet and the inches left over — with
 * miles. What is typed is kept as the fields are left, and reported in metres, which is how
 * the account keeps it; nothing whole typed yet reports null.
 */
export function HeightInput({
  heightM,
  units,
  onHeightM,
  size = 'text-base',
}: {
  heightM: number | null
  units: DistanceUnits
  onHeightM: (heightM: number | null) => void
  size?: string
}) {
  const [cm, setCm] = useState(heightM === null ? '' : String(Math.round(heightM * 100)))
  const opened = heightM === null ? null : cmToFeetInches(Math.round(heightM * 100))
  const [feet, setFeet] = useState(opened === null ? '' : String(opened.feet))
  const [inches, setInches] = useState(opened === null ? '' : String(opened.inches))

  const keep = (next: number | null) => {
    if (next !== heightM) {
      onHeightM(next)
    }
  }

  if (units === 'km') {
    return (
      <UnitInput
        value={cm}
        unit="cm"
        label="Height"
        size={size}
        step="1"
        onValue={setCm}
        onSettled={() => {
          const read = Number(cm)
          keep(
            cm.trim() !== '' && Number.isFinite(read) && read > 0 ? Math.round(read) / 100 : null,
          )
        }}
      />
    )
  }

  const settle = () => {
    const wholeFeet = Number(feet)
    const wholeInches = inches.trim() === '' ? 0 : Number(inches)
    keep(
      feet.trim() !== '' &&
        Number.isFinite(wholeFeet) &&
        Number.isFinite(wholeInches) &&
        wholeFeet > 0
        ? feetInchesToCm(Math.round(wholeFeet), Math.round(wholeInches)) / 100
        : null,
    )
  }
  return (
    <div className="grid grid-cols-2 gap-3">
      <UnitInput
        value={feet}
        unit="ft"
        label="Feet"
        size={size}
        step="1"
        onValue={setFeet}
        onSettled={settle}
      />
      <UnitInput
        value={inches}
        unit="in"
        label="Inches"
        size={size}
        step="1"
        onValue={setInches}
        onSettled={settle}
      />
    </div>
  )
}
